import { useCallback, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'
import { useSWRConfig } from 'swr'

import {
  standardRecipeTileFetcher,
  type MenuResponse,
  type StandardRecipeItem,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperienceMenuSWRKey } from './useMenuExperienceNavigation'

type MenuExperienceRecipeTileRefreshQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

type UseMenuExperienceRecipeTileRefreshArgs = {
  menuId?: string
  query: MenuExperienceRecipeTileRefreshQuery
}

const replaceStandardRecipeTile = (
  menu: MenuResponse,
  updatedRecipe: StandardRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) =>
    contentBlock.type === 'recipe-grid'
      ? {
          ...contentBlock,
          items: contentBlock.items.map((recipe) =>
            recipe.id === updatedRecipe.id ? updatedRecipe : recipe,
          ),
        }
      : contentBlock,
  ),
})

export const useMenuExperienceRecipeTileRefresh = ({
  menuId,
  query,
}: UseMenuExperienceRecipeTileRefreshArgs) => {
  const { mutate } = useSWRConfig()
  const deliveryDate = useSelector(getBasketDate)
  const basketNumPortions = useSelector(getNumPortions)
  const latestRequestByRecipeId = useRef<Partial<Record<RecipeUUID, symbol>>>({})

  const refreshRecipeTile = useCallback(
    async (recipeId: RecipeUUID, numPortions: number) => {
      const menuSWRKey = getMenuExperienceMenuSWRKey({
        deliveryDate,
        numPortions: basketNumPortions,
        query,
      })

      if (!menuId || !menuSWRKey) {
        return
      }

      const requestId = Symbol('menu-experience-recipe-tile-refresh')
      latestRequestByRecipeId.current[recipeId] = requestId

      try {
        const updatedRecipe = await standardRecipeTileFetcher({
          recipeId,
          menuId,
          numPortions,
          deliveryDate,
        })

        if (latestRequestByRecipeId.current[recipeId] !== requestId) {
          return
        }

        await mutate(
          menuSWRKey,
          (menu: MenuResponse | undefined) =>
            menu ? replaceStandardRecipeTile(menu, updatedRecipe) : menu,
          { revalidate: false },
        )
      } catch {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }
    },
    [basketNumPortions, deliveryDate, menuId, mutate, query],
  )

  return { refreshRecipeTile }
}
